import { api, decodeVaultBlobResponse } from '../api/client';
import { getAppState } from '../app/index';
import { decodeBase64 } from './primitives';
import { showBlockingConfirmDialog } from '../ui/confirmDialog';
import {
  checksumUserPublicKey,
  decryptVaultPlaintext,
  hasVaultVersionRegression,
} from './vault';
import { loadVaultSession } from './vaultSession';
import { persistVaultPlaintext } from './vaultPersist';
import { wrapKey } from './wrappedKey';

export type UserPublicKeyPinStatus = 'pinned' | 'new_pin' | 'changed';

export interface UserPublicKeyPinEvaluation {
  status: UserPublicKeyPinStatus;
  publicKey: Uint8Array;
  checksum: string;
}

export async function evaluateUserPublicKeyPin(
  userId: string,
  publicKey: Uint8Array,
  knownChecksums: Record<string, string>,
): Promise<UserPublicKeyPinEvaluation> {
  const checksum = await checksumUserPublicKey(publicKey);
  const known = knownChecksums[userId];

  if (!known) {
    return { status: 'new_pin', publicKey, checksum };
  }

  if (known === checksum) {
    return { status: 'pinned', publicKey, checksum };
  }

  return { status: 'changed', publicKey, checksum };
}

export async function confirmChangedPublicKey(): Promise<boolean> {
  return showBlockingConfirmDialog({
    titleKey: 'vault.pubKeyChange.title',
    messageKey: 'vault.pubKeyChange.body',
    confirmLabelKey: 'vault.pubKeyChange.confirm',
    cancelLabelKey: 'vault.pubKeyChange.cancel',
    confirmClassName: 'primary',
  });
}

export async function pinUserPublicKey(
  userId: string,
  checksum: string,
): Promise<void> {
  await persistVaultPlaintext(userId, (plaintext) => ({
    ...plaintext,
    knownUserPubChecksums: {
      ...plaintext.knownUserPubChecksums,
      [userId]: checksum,
    },
  }));
}

export async function resolveUserPublicKeyForWrap(
  userId: string,
  serverVaultVersion: number,
): Promise<Uint8Array> {
  const state = getAppState();
  if (state.kind !== 'authenticated') {
    throw new Error('not_authenticated');
  }

  if (hasVaultVersionRegression(state.session.userId, serverVaultVersion)) {
    throw new Error('vault_version_regression');
  }

  const directory = await api.getKeyDirectory();
  const entry = directory.find((item) => item.userId === userId);
  if (!entry) {
    throw new Error('key_directory_user_not_found');
  }

  const publicKey = decodeBase64(entry.userPublicKey);
  const session = loadVaultSession();
  if (!session) {
    throw new Error('vault_locked');
  }

  const blob = decodeVaultBlobResponse(await api.getVaultBlob());
  const plaintext = await decryptVaultPlaintext(session.vaultKey, blob.vaultBlob);
  const evaluation = await evaluateUserPublicKeyPin(userId, publicKey, plaintext.knownUserPubChecksums);

  if (evaluation.status === 'changed') {
    const confirmed = await confirmChangedPublicKey();
    if (!confirmed) {
      throw new Error('pub_key_not_confirmed');
    }

    await pinUserPublicKey(userId, evaluation.checksum);
    return publicKey;
  }

  if (evaluation.status === 'new_pin') {
    await pinUserPublicKey(userId, evaluation.checksum);
  }

  return publicKey;
}

/** Wrap key material to another user's public key after pin / regression checks. */
export async function wrapKeyForUser(
  userId: string,
  keyMaterial: Uint8Array,
  contextAad: string,
  _serverVaultVersion?: number,
): Promise<Uint8Array> {
  const status = await api.getVaultStatus();
  const userPublicKey = await resolveUserPublicKeyForWrap(userId, status.vaultVersion);
  return wrapKey(keyMaterial, userPublicKey, contextAad);
}

export async function confirmVaultRegression(): Promise<boolean> {
  return showBlockingConfirmDialog({
    titleKey: 'vault.regression.title',
    messageKey: 'vault.regression.body',
    confirmLabelKey: 'vault.regression.confirm',
    cancelLabelKey: 'vault.regression.cancel',
    confirmClassName: 'primary',
  });
}
